import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { alertOpen, AlertValue } from "../../features/alert.slice";
import { auth } from "../../firebase";
import userQuery from "../../firestore/User.query";
import { initialUserDataInfo, UserData, ColorList, GroupsSetting, APP_USE, GetAppUseStateFromUserData } from "../../model/Users";
import { UserRef } from "./UserRef";
import companiesQuery from "../../firestore/Companies.query";
import { Company } from "../../model/Companies";
import { Groups } from "../../model/Groups";
import { Spiner } from "../../components/spinner/Spiner";
import Button, { BtnProps } from "../../components/button/Button";
import { UserEditModal } from "./UserEditModal";
import { GroupSelectModal } from "./GroupSelectModal";
import { FamilyEmailForm, FamilyEmailModal } from "./FamilyEmailModal";
import { useInput } from "../../hooks/useInput";
import { CheckBoxItem } from "../../components/checkBox/CheckBoxGroup";
import { ChangeAccountSuspensionModal } from "./ChangeAccountSuspensionModal";
import { ModalAndAlmightyDialog } from "../../components/modal/ModalAndAlmightyDialog";
import { GridCol, GridRow } from "../../components/table/TableList";
import { LoginInfoPdfDownloadValidateModal } from "./LoginInfoPdfDownloadValidateModal";
import { pdf } from '@react-pdf/renderer';
import { LoginInfoPDF } from "../../components/pdf/LoginInfoPDF";
import { Keys } from "../../model/Keys";
import { Result } from "../../model/Result";
import { requestPasswordDecrypto } from "../../api/Decrypto.api";
import QRCode from "qrcode";
import { ConstMessage } from "../../config/const";
import { HistoryDisplayTab } from "./HistoryDisplayTab";
import { checkOption } from "../../controller/OptionController";

export const UserInfo = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;
  // 利用者番号
  const { userId } = useParams<{ userId: string }>();

  const [isDisabled, setIsDisabled] = useState(false);
  const [userInfo, setUserInfo] = useState(initialUserDataInfo);
  const [company, setCompany] = useState<Company>();
  const [companyGroups, setCompanyGroups] = useState<Groups[]>([]);
  const [isLoad, setIsLoad] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenGroupSelect, setIsOpenGroupSelect] = useState(false);
  const [isOpenFamilyEmail, setIsOpenFamilyEmail] = useState(false);
  const [isOpenAccountSuspension, setIsOpenAccountSuspension] = useState(false);
  const [companyGroupsValue, setCompanyGroupsValue] = useState<CheckBoxItem[]>([]);
  const [companyGroupNames, setCompanyGroupNames] = useState<string[]>([]);
  const [familyEmails, setFamilyEmails] = useState<string[] | undefined>([]);
  const [isVoid, setIsVoid] = useState(false);
  const [activeUserCount, setActiveUserCount] = useState<number>(0);
  const [inactiveUserCount, setInactiveUserCount] = useState<number>(0);
  const [beforeUsers, setBeforeUsers] = useState<UserData>();
  const [afterUsers, setAfterUsers] = useState<UserData>();
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState<string>('');
  const [dialogChildren, setDialogChildren] = useState<React.ReactNode | undefined>();
  const [dialogActions, setDialogActions] = useState<BtnProps[]>([]);
  const [pdfDownloadErrorMessages, setPdfDownloadErrorMessages] = useState<{name: string, message: string}[]>([]);
  const [isOpenLoginInfoPdfDownloadValidate, setIsOpenLoginInfoPdfDownloadValidate] = useState(false);
  const [searchKey1Selected, setSearchKey1Selected] = useState<string | undefined>();
  const [searchKey1List, setSearchKey1List] = useState<{ value: string; disabled: boolean; }[]>([]);
  const [searchKey2Selected, setSearchKey2Selected] = useState<string | undefined>();
  const [searchKey2List, setSearchKey2List] = useState<{ value: string; disabled: boolean; }[]>([]);
  const [searchKey3Selected, setSearchKey3Selected] = useState<string | undefined>();
  const [searchKey3List, setSearchKey3List] = useState<{ value: string; disabled: boolean; }[]>([]);
  const [optionCheck, setOptionCheck] = useState(false);

  useEffect(() => {
    // スクロールポジションの初期化
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const f = async () => {
      if (userId !== undefined) {
        // 団体を取得
        const company = await companiesQuery.getCompany(currentUser?.tenantId!);
        setCompany(company);

        // アプリ利用可能件数取得
        const appAvailable = await userQuery.getAppAvailableLength(currentUser?.tenantId!);
        // アプリ利用不可件数取得
        const appInavailable = await userQuery.getAppUnavailableLength(currentUser?.tenantId!);
        setInactiveUserCount(appInavailable);
        // 団体の端末紐づけユーザー数を取得
        setActiveUserCount(appAvailable);

        // 団体に登録されているグループを取得
        const companyGroups = await companiesQuery.getGroups(currentUser?.tenantId!);
        const companyGroupNames = companyGroups.map((item) => item.name);
        setCompanyGroupNames(companyGroupNames);
        // 利用者情報を取得
        const user = await userQuery.getUser(currentUser?.tenantId!, userId) as UserData;
        setBeforeUsers({...user});
        setSearchKey1Selected(user.searchKey1 === undefined ? '' : user.searchKey1);
        setSearchKey2Selected(user.searchKey2 === undefined ? '' : user.searchKey2);
        setSearchKey3Selected(user.searchKey3 === undefined ? '' : user.searchKey3);
        // グループを取得
        const userGroupList = await userQuery.getGroups(user.id);
        const displayUserGroupList = userGroupList.filter(
          (group) => group.id !== "all" && group.id !== company.displayId);
        user.groups = await Promise.all(displayUserGroupList.map((group) => {
          const companyGroup = companyGroups.filter((companyGroup) => companyGroup.id === group.id)[0];
          return {
            id: group.id,
            name: companyGroup.name,
            color: ColorList[companyGroupNames.indexOf(companyGroup.name) % Object.keys(ColorList).length] as GroupsSetting["color"],
            disabled: companyGroup.deleteFlg,
          }
        }));
        user.groupCount = user.groups.length;
        setUserInfo(user);
        setCompanyGroups(companyGroups);

        // ご家族のEmail
        setFamilyEmails(userInfo.familyEmail?.split(","));

        // 検索キー1,2,3取得
        const searchKeys = await companiesQuery.getSearchKeys(company.id);
        const emptyValue = [{value: '', disabled: false}];
        if (searchKeys.length > 0) {
          searchKeys.map((item) => {
            const keyValues = item.keyValues;
            if (keyValues.length > 0) {
              keyValues.sort((a, b) => a.sort - b.sort);
              const searchKeyList = [...keyValues].map(obj => ({
                value: obj.searchName,
                disabled: false
              }));
              searchKeyList.unshift({value: '', disabled: false});
              const searchNameList = [...keyValues].map(obj => obj.searchName);
              searchNameList.unshift('');
              if (item.name === "userSearchKey1") {
                const searchKey1SelectList = [...searchKeyList];
                if (user.searchKey1 !== undefined && user.searchKey1 !== '' && searchNameList.includes(user.searchKey1) === false) {
                  searchKey1SelectList.unshift({value: user.searchKey1, disabled: true});
                }
                setSearchKey1List(searchKey1SelectList);
              } else if (item.name === "userSearchKey2") {
                const searchKey2SelectList = [...searchKeyList];
                if (user.searchKey2 !== undefined && user.searchKey2 !== '' && searchNameList.includes(user.searchKey2) === false) {
                  searchKey2SelectList.unshift({value: user.searchKey2, disabled: true});
                }
                setSearchKey2List(searchKey2SelectList);
              } else if (item.name === "userSearchKey3") {
                const searchKey3SelectList = [...searchKeyList];
                if (user.searchKey3 !== undefined && user.searchKey3 !== '' && searchNameList.includes(user.searchKey3) === false) {
                  searchKey3SelectList.unshift({value: user.searchKey3, disabled: true});
                }
                setSearchKey3List(searchKey3SelectList);
              }
            } else {
              if (item.name === "userSearchKey1") {
                setSearchKey1List(emptyValue);
              } else if (item.name === "userSearchKey2") {
                setSearchKey2List(emptyValue);
              } else if (item.name === "userSearchKey3") {
                setSearchKey3List(emptyValue);
              }  
            }
          })
        } else {
          setSearchKey1List(emptyValue);
          setSearchKey2List(emptyValue);
          setSearchKey3List(emptyValue);
        }
        if (searchKey1List.length === 0) {
          setSearchKey1List(emptyValue);
        }
        if (searchKey2List.length === 0) {
          setSearchKey2List(emptyValue);
        }
        if (searchKey3List.length === 0) {
          setSearchKey3List(emptyValue);
        }

        // ポイント管理のオプションが有効か
        setOptionCheck(await checkOption("point", dispatch, false));

        setIsLoad(false);
      } else {
        dispatch(
          alertOpen(AlertValue("ネットワークに接続できません。", "danger"))
        );
      }
    }
    f();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[currentUser?.tenantId, dispatch, userId, isOpen]);

  // 登録
  const onSubmit = async (user: UserData) => {
    if(!isDisabled){
      try {
        setIsDisabled(true);
        user.groups = userInfo.groups;
        user.familyEmail = familyEmails?.join(',');
        if (afterUsers?.accountSuspensionFlg) {
          toggleAccountSuspension();
        }

        // 設定している所属グループ
        const groupIds = user.groups.map((g) => g.id);
        for (const groupId of groupIds) {
          // グループ数取得
          const count: number = await userQuery.getGroupUserCount(currentUser?.tenantId!, groupId);
          // 自分がすでにそのグループにいたら1引く
          const alreadyInGroup = Array.isArray(beforeUsers?.groups)
            ? beforeUsers!.groups.some((g) => g.id === groupId)
            : false;
          const adjustedCount = alreadyInGroup ? count - 1 : count;

          if (adjustedCount >= 1000) {
            dispatch(alertOpen(AlertValue(`グループ「${user.groups.find(g => g.id === groupId)?.name}」は所属上限の1000人を超えています。`, "danger")));
            setIsDisabled(false);
            return;
          }
        }
        
        if (afterUsers?.accountSuspensionFlg) {
          await userQuery.updateUser(currentUser?.tenantId!, afterUsers, company?.displayId!);
        } else {
          await userQuery.updateUser(currentUser?.tenantId!, user, company?.displayId!);
        }
        dispatch(alertOpen(AlertValue(`登録しました。`, "success")));
        toggle();
        setIsDisabled(false);
      } catch (err: any) {
        let errorMessage = err.code;
        if (errorMessage === "ERR_NETWORK") {
          dispatch(
            alertOpen(AlertValue("ネットワークに接続できません。", "danger"))
          );
        } else {
          console.error(err)
          dispatch(alertOpen(AlertValue(`${err.message}`, "danger")));
        }
        setIsDisabled(false);
      }
    }
  };

  const submitButtonClick = (user: UserData): Promise<void> => {
    if (GetAppUseStateFromUserData(user) !== APP_USE.SUSPEND) {
      onSubmit(user);
    } else {
      setAfterUsers(user);
      toggleAccountSuspension();
    }
    return Promise.resolve();
  }

  const toggle = () => {
    setIsOpen(!isOpen);
  };

  const toggleAccountSuspension = () => {
    setIsOpenAccountSuspension(!isOpenAccountSuspension);
  };

  const toggleLoginInfoPdfValidate = () => {
    setIsOpenLoginInfoPdfDownloadValidate(!isOpenLoginInfoPdfDownloadValidate);
  };

  // グループ選択
  const groups = useInput(companyGroupsValue);
  useEffect(() => {
    const f = async () => {
      setIsVoid(!isVoid);
      if(isOpenGroupSelect){
        const companyGroupsValue = await Promise.all(companyGroups.map((item): CheckBoxItem => {
          return {
            id: item.id,
            title: item.name,
            value: userInfo.groups.filter(group => group.id === item.id).length > 0,
            disabled: item.deleteFlg,
          }
        }));
        setCompanyGroupsValue(companyGroupsValue);
      }
      if(companyGroupsValue.length > 0){
        userInfo.groups = [];
        await Promise.all(companyGroupsValue.map((item: any) => {
          if(item.value){
            userInfo.groups.push({
              id: item.id,
              name: item.title,
              color: ColorList[companyGroupNames.indexOf(item.title) % Object.keys(ColorList).length] as GroupsSetting["color"],
              disabled: item.disabled,
            });
          }
          return userInfo;
        }));
      }
      setUserInfo(userInfo);
    };
    f();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[isOpenGroupSelect]);
  const toggleGroupSelect = () => {
    setIsOpenGroupSelect(!isOpenGroupSelect);
    setIsVoid(!isVoid);
  };

  // email登録
  const onEmailSubmit = (data: FamilyEmailForm) => {
    if(!isDisabled){
      try {
        setIsDisabled(true);
        setFamilyEmails(Object.values(data));
        toggleFamilyEmail();
        setIsDisabled(false);
      } catch (err: any) {
        console.error(err);
        dispatch(alertOpen(AlertValue("設定に失敗しました。", "danger")));
        setIsDisabled(false);
      }
    }
  };

  const toggleFamilyEmail = () => {
    setIsOpenFamilyEmail(!isOpenFamilyEmail);
  };

  const pdfErrorMessageCol: GridCol[] = [
    {
      field: "name",
      headerName: "項目名",
      style: ""
    },
    {
      field: "message",
      headerName: "エラーメッセージ",
      style: "white-space: pre-line"
    },
  ]

  const pdfErrorMessageRow: GridRow[] = pdfDownloadErrorMessages.map<GridRow>((error) => {
    return {
      rowData: {
        name: error.name,
        message: (
          <div style={{whiteSpace: "pre-line"}}>
            {error.message}
          </div>
        ),
      }
    }
  });

  const pdfDownloadValidate = () => {
    // PDFダウンロード有効チェック
    setPdfDownloadErrorMessages([]);
    let errorMessages = [];
    const deleteFlg = GetAppUseStateFromUserData(userInfo);
    if (!userInfo.lastName) {
      errorMessages.push({
        'name': '氏名', 
        'message': ConstMessage.ERR_LOGIN_INFO_PDF_LAST_NAME
      });
    }
    if (userInfo.admin === undefined) {
      errorMessages.push({
        'name': '役割', 
        'message': ConstMessage.ERR_LOGIN_INFO_PDF_ADMIN
      });
    }
    if (deleteFlg === undefined || deleteFlg === APP_USE.UNUSEABLE || deleteFlg === APP_USE.SUSPEND) {
      errorMessages.push({
        'name': 'アプリ利用', 
        'message': ConstMessage.ERR_LOGIN_INFO_PDF_DELETE_FLG
      });
    }
    if (errorMessages.length === 0) {
      pdfDownloadConfirmation();
    } else {
      setPdfDownloadErrorMessages(errorMessages);
      toggleLoginInfoPdfValidate();
    }
  }

  const pdfDownloadConfirmation = () => {
    // PDFエクスポート前確認ダイアログ
    const actions: BtnProps[] = [
      {
        outline: true,
        children: "キャンセル",
        className:"w-[132px]",
        onClick: () => {
          setIsOpenDialog(false);
          initDialog();
        }
      },
      {
        color: "indigo",
        children: "ダウンロード",
        onClick: () => {
          setIsOpenDialog(false);
          initDialog();
          loginIngoPdfDownload();
        }
      },
    ];
    const dialogChildren = (<>
      ログイン情報PDFをダウンロードしますか？
    </>)
    setIsOpenDialog(true);
    setDialogChildren(dialogChildren);
    setDialogActions(actions);
  }

  const toggleDialog = () => {
    setIsOpenDialog(isOpenDialog);
  };

  const initDialog = () => {
    setDialogTitle("");
    setDialogChildren("");
    setDialogActions([]);
  };

  const loginIngoPdfDownload = async () => {
    if (company) {
      setIsDisabled(true);
      // パスコード取得(暗号化済み)
      await userQuery.getPasscode(company.id!, userInfo.id).then((resolve) => {
        userInfo.encryptedPasscode = resolve[0].encryptedPasscode;
      });
      // パスワード復号化
      const keys: Keys = {
        encryptedPasscode: userInfo.encryptedPasscode!,
        userId: userInfo.id.substring(userInfo.id.indexOf(company.displayId) + company.displayId.length)
      }
      const result: Result = await requestPasswordDecrypto([keys], company.displayId);
      if (result.success) {
        // QRコード画像データ生成
        userInfo.decryptedPasscode = result.data[0].decryptedPasscode.replace(company.displayId, '');
        const userDsplayId = userInfo.id.substring(userInfo.id.indexOf(company.displayId) + company.displayId.length);
        const userName = userInfo.lastName;
        userInfo.lastName = nameDisp(userInfo.lastName ?? '');
        userInfo.lastNameKana = nameDisp(userInfo.lastNameKana ?? '');
        let qrCodeData: string = '';
        QRCode.toDataURL(`${company?.displayId}-${userDsplayId}-${userInfo.decryptedPasscode}`, { errorCorrectionLevel: 'H' })
          .then(async url => {
            qrCodeData = url;
            // PDFダウンロード
            const blob = await pdf(<LoginInfoPDF userInfo={userInfo} company={company} qrCodeData={qrCodeData}/>).toBlob();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `ログイン情報_${company ? userInfo.id.substring(userInfo.id.indexOf(company.displayId) + company.displayId.length) + '_' : ''}${userName}.pdf`;
            link.click();
            URL.revokeObjectURL(link.href);
          })
          .catch(err => {
            console.error(err);
            dispatch(
              alertOpen(AlertValue(ConstMessage.ERR_LOGIN_INFO_PDF_DOWNLOAD, "danger"))
            );
          });
          setIsDisabled(false);
      } else {
        dispatch(
          alertOpen(AlertValue(ConstMessage.ERR_LOGIN_INFO_PDF_DOWNLOAD, "danger"))
        );
        setIsDisabled(false);
      }
    }
  }

  // 最大桁数を判定
  const nameDisp = (name: string) => {
    const kanaRegex = new RegExp(/[ｦ-ﾟ]/) 
    let nameLength = 0;
    let newName = "";
    for(let i = 0; i < name.length; i++){
      // 半角カナ・半角英数を判定
      if(name[i].match(kanaRegex)){
        nameLength = nameLength + 0.5;
      }else if(name[i].match(/^[a-z0-9０-９]*$/)){
        nameLength = nameLength + 0.875;
      }else{
        nameLength = nameLength + 1;
      }

      if(nameLength <= 14){
        newName = newName + name[i];
      }
    }
    return newName;
  }

  const onSearchKey1Select = (value: string) => {
    setSearchKey1Selected(value);
  }
  const onSearchKey2Select = (value: string) => {
    setSearchKey2Selected(value);
  }
  const onSearchKey3Select = (value: string) => {
    setSearchKey3Selected(value);
  }

  return (<>
    <div className="flex h-full">
      <div className="relative z-0 flex flex-1">
        <main className="relative z-0 flex-1 focus:outline-none xl:order-last">
          {/* Breadcrumb */}
          <nav
            className="flex items-start px-4 py-3"
            aria-label="Breadcrumb"
          >
            <div className="inline-flex items-center space-x-3 text-sm font-medium text-gray-900 cursor-pointer">
              <span onClick={() => navigate("/userList")} className="flex items-center">
                <ChevronLeftIcon className="-ml-2 h-5 w-5 text-gray-400 mr-3" />一覧へ</span>
            </div>
          </nav>
          {isLoad ? <Spiner/> : (
            <>
              <UserRef
                userData={userInfo}
                company={company!}
                appUse={GetAppUseStateFromUserData(userInfo)}
                option={optionCheck}
              />
              <div className="mx-6">
                <div className="max-w-5xl sm:px-6">
                  <div className="grid gap-x-36 sm:grid-cols-2">
                    <div></div>
                    <div className={'flex mb-8'}>
                      <Button
                        size={"sm"}
                        className={"min-w-fit"}
                        color={"violet"}
                        onClick={() => pdfDownloadValidate()}
                        disabled={isDisabled}
                      >
                        ログイン情報<br/>PDF出力
                      </Button>
                      <Button
                        size={"sm"}
                        className={"ml-2 min-w-fit"}
                        color={"yellow"}
                        onClick={() => navigate(`/userList/${userInfo.id}/EditPassword`)}
                      >
                        パスワード<br/>確認・変更
                      </Button>
                      <Button
                        size={"sm"}
                        className={"ml-2 min-w-fit items-center"}
                        onClick={toggle}
                      >
                        編集
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <HistoryDisplayTab
                targetUserId={userInfo.displayId}
                companyDisplayId={company!.displayId}
                company={company}
              />
              <GroupSelectModal
                open={isOpenGroupSelect}
                toggle={toggleGroupSelect}
                closeOnClickOutside
                companyGroupsValue={companyGroupsValue}
                groups={groups}
                setState={setCompanyGroupsValue}
                children={undefined}
              />
              <FamilyEmailModal
                open={isOpenFamilyEmail}
                toggle={toggleFamilyEmail}
                closeOnClickOutside
                familyEmailValue={familyEmails}
                children={undefined}
                onSubmit={onEmailSubmit}
              />
              <UserEditModal
                open={isOpen}
                toggle={toggle}
                onSubmit={submitButtonClick}
                isVoid={isVoid}
                toggleGroupSelect={toggleGroupSelect}
                toggleFamilyEmail={toggleFamilyEmail}
                closeOnClickOutside={true}
                children={undefined}
                user={userInfo}
                familyEmails={familyEmails}
                companyDisplayId={company!.displayId}
                onSearchKey1Select={onSearchKey1Select}
                searchKey1Selected={searchKey1Selected}
                searchKey1List={searchKey1List}
                onSearchKey2Select={onSearchKey2Select}
                searchKey2Selected={searchKey2Selected}
                searchKey2List={searchKey2List}
                onSearchKey3Select={onSearchKey3Select}
                searchKey3Selected={searchKey3Selected}
                searchKey3List={searchKey3List}
                tenantId={company ? company.id : ''}
              />
              <ChangeAccountSuspensionModal
                open={isOpenAccountSuspension}
                toggle={toggleAccountSuspension}
                closeOnClickOutside={true}
                children={undefined}
                onSubmit={onSubmit}
              />
              <LoginInfoPdfDownloadValidateModal
                open={isOpenLoginInfoPdfDownloadValidate}
                toggle={toggleLoginInfoPdfValidate}
                closeOnClickOutside={true}
                children={undefined}
                col={pdfErrorMessageCol}
                row={pdfErrorMessageRow}
              />
              <ModalAndAlmightyDialog
                toggle={toggleDialog}
                open={isOpenDialog}
                title={dialogTitle}
                children={dialogChildren}
                actions={dialogActions}
                height={"72"}
                closeOnClickOutside={false}
              />
            </>
          )}
        </main>
      </div>
    </div>
  </>);
}
